// Clone chi tiết giao dịch thu nhập (Thông tin chung / Chi tiết / Tổng hợp / Giải ngân)
// Bám sát mobile-app-v2: modules/finance/income/{detail,compareDetail,paidDetail}.tsx
import React, { useEffect, useState } from "react";
import { Box, Collapse, Flex, Spinner, Text } from "@chakra-ui/react";
import moment from "moment";
import { appImages } from "../../assets/appicons";
import { formatMoney, formatMoneyUS } from "../../utils/financeFormat";
import { getIncomePaidDisbursementApi } from "../../api/finance.income.api";
import { EMP_BASE_PATH } from "../../config";

// ===== màu khớp theme app =====
const PRIMARY = "#4081EB"; // primary200
const PRIMARY2 = "#1A9ED0";
const SUBTITLE = "#51607B";
const BLACK = "#000000"; // baseContent
const BLUE_LIGHT = "#DAE0EB";
const ORANGE = "#FF9000";

const JOB = { TGD: "TGD", TRUONG_VUNG: "TRUONG_VUNG", TRUONG_KHOI: "TRUONG_KHOI" };
const codeIn = (user, arr) => arr.includes(user?.jobPosition?.code);

// ===== format khớp app =====
// Tên/mã hiển thị: BE v1 dựng "employeeObject" làm object hiển thị (spread full Employee),
// còn manager/employee chỉ để lọc → ưu tiên employeeObject, fallback manager/employee (khớp app v2).
const nameOf = (x) => x?.employeeObject?.fullName || x?.manager?.fullName || x?.employee?.fullName || "";
const codeOf = (x) => x?.employeeObject?.code || x?.manager?.code || x?.employee?.code || "";

const fmtK = (v) => String(Number(Number(v || 0).toFixed(4)));
const money = (v) => `${formatMoneyUS(v)} VNĐ`; // dấu phẩy (formatMoneyToIn)
const moneyDot = (v) => `${formatMoney(v)} VNĐ`; // dấu chấm (formatPrice @utils)
const fmtDate = (v) => (v ? moment(v).format("DD/MM/YYYY") : "-");

const Chevron = ({ open }) => (
  <img
    src={appImages.icon_chevron_down}
    alt=""
    width={20}
    height={20}
    style={{ objectFit: "contain", transform: open ? "rotate(0deg)" : "rotate(270deg)", transition: "transform .15s" }}
  />
);

// Hàng thông tin tab "Thông tin chung" (IncomePropCell)
export const IncomePropCell = ({ label, value, onPress, borderBottom = true }) => (
  <Flex py="18px" justify="space-between" align="flex-start" gap="12px" borderBottom={borderBottom ? "0.5px solid #E1E1E1" : "none"}>
    <Text flex="0.42" fontSize="14px" color={SUBTITLE}>{label}</Text>
    <Text
      as={onPress ? "button" : "span"}
      onClick={onPress}
      flex="0.58"
      fontSize="14px"
      fontWeight={600}
      color={onPress ? PRIMARY : BLACK}
      textAlign="right"
      whiteSpace="pre-line"
      display="inline"
    >
      {value !== undefined && value !== null && value !== "" ? String(value) : "-"}
    </Text>
  </Flex>
);

// Hàng PropsCell của các mục con (Tổng hợp / Chi tiết / Giải ngân)
const PropRow = ({ label, value, labelColor = SUBTITLE, valueColor = BLACK, mt = 0, size = 0.4 }) => (
  <Flex mt={`${mt}px`} align="flex-start">
    <Box w={`${size * 100}%`}>
      <Text fontSize="14px" fontWeight={600} color={labelColor} textAlign="left">{label}</Text>
    </Box>
    <Box flex="1" display="flex" justifyContent="flex-end">
      <Text fontSize="14px" fontWeight={600} color={valueColor} textAlign="right">
        {value !== undefined && value !== null && value !== "" ? String(value) : "-"}
      </Text>
    </Box>
  </Flex>
);

// Khung mục có thể mở/đóng
const Section = ({ title, titleColor = BLACK, defaultOpen = false, bare = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box>
      <Flex
        as="button"
        type="button"
        w="100%"
        onClick={() => setOpen((p) => !p)}
        justify="space-between"
        align="center"
        p="16px"
        border="1px solid"
        borderColor={BLUE_LIGHT}
        borderTopRadius="8px"
        borderBottomRadius={open ? "0" : "8px"}
      >
        <Text fontSize="16px" fontWeight={600} color={titleColor} textAlign="left">{title}</Text>
        <Chevron open={open} />
      </Flex>
      <Collapse in={open} animateOpacity>
        {bare ? (
          <Box w="100%">{children}</Box>
        ) : (
          <Box w="100%" p="16px" borderLeft="1px solid" borderRight="1px solid" borderBottom="1px solid" borderColor={BLUE_LIGHT} borderBottomRadius="8px">
            {children}
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

const Divider08 = () => <Box borderTop="0.8px solid" borderColor={BLUE_LIGHT} />;

// Danh sách các đợt đối chiếu để render (nhiều đợt => tách, không thì giữ 1 đợt như cũ)
const getReconPeriods = (data) =>
  data?.periods && data.periods.length > 0 ? data.periods : [data];

// Nhãn "ĐC T{MM}.{YYYY} - Đợt {n}" giống FE trang Quản lý phí dịch vụ
const reconPeriodLabel = (p, idx) =>
  `ĐC ${p?.matchingDate ? moment(p.matchingDate).format("[T]MM.YYYY") : moment().format("[T]MM.YYYY")} - Đợt ${p?.period ?? idx + 1}`;

// Khối gập cho từng đợt đối chiếu (chỉ hiển thị header khi căn có nhiều đợt)
const PeriodReconSection = ({ title, index, children }) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <Box mt="16px">
      <Flex as="button" type="button" w="100%" onClick={() => setOpen((v) => !v)} justify="space-between" align="center" p="16px" border="1px solid" borderColor={PRIMARY} borderRadius="8px">
        <Text fontSize="16px" fontWeight={600} color={PRIMARY} textAlign="left">{title}</Text>
        <Chevron open={open} />
      </Flex>
      <Collapse in={open} animateOpacity>
        <Box mt="16px">{children}</Box>
      </Collapse>
    </Box>
  );
};

/* =========================================================
 * TAB 1 — THÔNG TIN CHUNG
 * =======================================================*/
export const OverviewTab = ({ data, kind, user, navigate }) => {
  const lk = data?.locking || {};
  const info = data?.informationForm || {};
  const isShow = user?._id && lk?.bookerId && user._id === lk.bookerId;
  const feeLabel = kind === "provisional" ? "Giá tính PDV tạm tính" : "Giá tính PDV";
  const lockingId = kind === "provisional" ? lk?._id : data?.lockingId;
  const go = (path, id) => (id ? () => navigate(`${EMP_BASE_PATH}/${path}/${id}`) : undefined);

  return (
    <Box pt="16px" px="16px" pb="20px">
      <Text fontSize="16px" fontWeight={600} color={BLACK}>
        {`${lk?.projectObject?.name || ""} ${lk?.projectObject?.name ? "-" : ""} ${lk?.apartmentObject?.code || ""}`.trim()}
      </Text>

      <Box>
        <IncomePropCell label="Dự án" value={lk?.projectObject?.name || "-"} onPress={go("tai-chinh/du-an", lk?.projectId)} />
        <IncomePropCell label="Đợt mở bán" value={lk?.saleOpeningEvent?.name || "-"} onPress={go("tai-chinh/dot-mo-ban", lk?.saleOpeningEventId)} />
        <IncomePropCell label="Mã căn" value={`${lk?.apartmentObject?.code} (${lk?.apartmentObject?.name})`} />
        <IncomePropCell label="Giá trị niêm yết" value={moneyDot(lk?.totalValue || 0)} />
        <IncomePropCell label={feeLabel} value={money(info?.priceForCommission || 0)} />
        <IncomePropCell label="Khách hàng" value={lk?.customer?.name || "-"} onPress={go("tai-chinh/khach-hang", lk?.customerId)} />
        <IncomePropCell label="Ngày cọc" value={fmtDate(lk?.transactionDate)} />
        <IncomePropCell label="Công chuẩn" value={fmtK(info?.standardWorkingDaysInMonth || 0)} />
        <IncomePropCell label="Tỷ lệ giao dịch" value={`${fmtK(info?.transactionRate || 0)}%`} />
        <IncomePropCell label="Thanh toán dự án" value={`${fmtK(info?.calculateCommissionRate || 0)}%`} />
        <IncomePropCell label="Tỷ lệ thưởng nóng" value={`${fmtK(info?.incomeRate || 0)}%`} />
      </Box>

      {isShow && (
        <IncomePropCell label="Booking" value={lk?.bookingId ? "Xem chi tiết" : "-"} onPress={go("tai-chinh/booking", lk?.bookingId)} />
      )}
      {isShow && (
        <IncomePropCell label="Locking" value={lockingId ? "Xem chi tiết" : "-"} onPress={go("tai-chinh/locking", lockingId)} />
      )}
    </Box>
  );
};

/* =========================================================
 * TAB 2 — CHI TIẾT (Participants / Blocks / Areas / Referrals / Managers)
 * =======================================================*/
const ParticipantCard = ({ main, found, index, count }) => {
  const [open, setOpen] = useState(true);
  const sum = (x) =>
    Number(x?.incomePrice || 0) + Number(x?.salesCommissionPrice || 0) + Number(x?.eliteSalesCommissionPrice || 0);
  const totalCommissionPrice = sum(main) + sum(found);
  const personRows = (item, nameLabel, codeLabel) => (
    <>
      <PropRow label={nameLabel} value={item?.employee?.fullName || item?.employeeObject?.fullName || ""} />
      <PropRow label={codeLabel} value={item?.employee?.code || item?.employeeObject?.code || ""} mt={10} />
      <PropRow label="Diễn giải" value={item?.interpretation || ""} mt={10} />
      <PropRow label="Ngày công thực tế" value={item?.workingDays || "0"} mt={10} />
      <PropRow label="Tỷ lệ giao dịch" value={item?.commissionRate ? `${item.commissionRate}%` : "0%"} mt={10} />
      <PropRow label="Số tiền thưởng nóng sàn" value={money(item?.incomePrice || 0)} mt={10} />
      <PropRow label="Số tiền CVKD" value={money(item?.salesCommissionPrice || 0)} mt={10} />
      <PropRow label="Số tiền CVKD Elite" value={money(item?.eliteSalesCommissionPrice || 0)} mt={10} />
    </>
  );
  return (
    <Box mt="16px">
      <Flex
        as="button"
        type="button"
        w="100%"
        onClick={() => setOpen((p) => !p)}
        justify="space-between"
        align="center"
        p="16px"
        border="1px solid"
        borderColor={BLUE_LIGHT}
        borderTopRadius="8px"
        borderBottomRadius={open ? "0" : "8px"}
      >
        <Text fontSize="16px" fontWeight={600} color={BLACK} textAlign="left">
          {`Thông tin chuyên viên kinh doanh ${count > 1 ? index + 1 : ""}`.trim()}
        </Text>
        <Chevron open={open} />
      </Flex>
      <Collapse in={open} animateOpacity>
        <Box w="100%" p="16px" borderLeft="1px solid" borderRight="1px solid" borderBottom="1px solid" borderColor={BLUE_LIGHT} borderBottomRadius="8px">
          <Box>{personRows(main, "Tên CVKD", "Mã CVKD")}</Box>

          {found && (
            <>
              <Box h="16px" />
              <Divider08 />
              <Box h="16px" />
              <Box>{personRows(found, "Tên CTV", "Mã CTV")}</Box>
            </>
          )}

          <Box h="16px" />
          <Box borderTop="1px solid" borderColor={PRIMARY}>
            <PropRow label="Tổng PDV" value={money(totalCommissionPrice || 0)} labelColor={PRIMARY} valueColor={PRIMARY} mt={10} />
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

const SectionParticipants = ({ data }) => {
  const list = data?.relativeParticipantForm?.list || [];
  // CTV luôn được đánh cờ isCollaborator === true; mọi item còn lại là CVKD chính.
  // Dùng "!== true" thay vì "=== false" để không bỏ sót main khi cờ bị thiếu/undefined.
  const ctvs = list.filter((x) => x?.isCollaborator === true);
  const mains = list.filter((x) => x?.isCollaborator !== true);
  if (!mains.length) return null;
  return (
    <>
      {mains.map((main, index) => {
        const found = ctvs.find((c) => String(main?.employeeId) === String(c?.mainSellerId));
        return <ParticipantCard key={index} main={main} found={found} index={index} count={mains.length} />;
      })}
    </>
  );
};

// Mục Giám đốc khối / vùng (item có collapsible con)
const ManagerGroupSection = ({ title, form, prefix, totalRateLabel, totalRate, totalPriceLabel, totalPrice, show }) => {
  const [openItems, setOpenItems] = useState({});
  useEffect(() => {
    const r = {};
    (form?.list || []).forEach((b, bi) => (b?.items || []).forEach((_, ii) => (r[`${bi}-${ii}`] = true)));
    setOpenItems(r);
  }, [form]);
  if (!show) return null;
  const label3 = (interp) =>
    (interp === `${prefix}DH` ? `${prefix}DH của` : interp === `${prefix}DT` ? `${prefix}DT của` : `${prefix}CL của`);
  return (
    <Section title={title}>
      {(form?.list || []).map((block, bi) => (
        <Box key={bi}>
          {(block?.items || []).map((item, ii) => {
            const key = `${bi}-${ii}`;
            const open = !!openItems[key];
            return (
              <Box key={ii} borderTop={ii !== 0 ? "0.8px solid" : "none"} borderColor={BLUE_LIGHT}>
                <Box
                  as="button"
                  type="button"
                  w="100%"
                  pt={ii !== 0 ? "16px" : "0"}
                  onClick={() => setOpenItems((p) => ({ ...p, [key]: !p[key] }))}
                >
                  <Flex justify="space-between" align="center">
                    <Text fontSize="14px" fontWeight={600} color={PRIMARY} textAlign="left">
                      {`${label3(item?.interpretation)} ${block?.employee?.fullName || ""} / ${block?.employee?.code || ""}`}
                    </Text>
                    <Chevron open={open} />
                  </Flex>
                </Box>
                <Collapse in={open} animateOpacity>
                  <Box w="100%" pb="16px">
                    <PropRow label="Tên giám đốc" value={nameOf(item)} mt={10} />
                    <PropRow label="Mã giám đốc" value={codeOf(item)} mt={10} />
                    <PropRow label="Ngày công thực tế" value={item?.standardWorkingDaysInMonth?.toString() || "0"} mt={10} />
                    <PropRow label="Tỷ lệ" value={item?.commissionRate != null ? `${fmtK(item.commissionRate)}%` : "0%"} mt={10} />
                    <PropRow label="Số tiền thưởng nóng" value={money(item?.commissionPrice || 0)} mt={10} />
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      ))}
      <Box borderTop="1px solid" borderColor={PRIMARY}>
        <Box h="10px" />
        <PropRow label={totalRateLabel} value={`${fmtK(totalRate || 0)}%`} labelColor={PRIMARY} valueColor={PRIMARY} />
        <PropRow label={totalPriceLabel} value={money(totalPrice || 0)} labelColor={PRIMARY} valueColor={PRIMARY} mt={10} />
      </Box>
    </Section>
  );
};

const SectionManagers = ({ data, user }) => {
  const form = data?.managersForm;
  if (!codeIn(user, [JOB.TGD])) return null;
  return (
    <Section title="Thông tin tổng giám đốc">
      {(form?.list || []).map((block, bi) => (
        <Box key={bi} pb="16px" borderTop={bi !== 0 ? "0.8px solid" : "none"} borderColor={BLUE_LIGHT}>
          <PropRow label="Tên tổng giám đốc" value={nameOf(block)} />
          <PropRow label="Mã tổng giám đốc" value={codeOf(block)} mt={10} />
          <PropRow label="Tỷ lệ" value={block?.commissionRate != null ? `${fmtK(block.commissionRate)}%` : "0%"} mt={10} />
          <PropRow label="Số tiền thưởng nóng" value={money(block?.commissionPrice || 0)} mt={10} />
        </Box>
      ))}
      <Box borderTop="1px solid" borderColor={PRIMARY}>
        <Box h="10px" />
        <PropRow label="Tổng tỷ lệ giao dịch" value={`${fmtK(form?.totals?.totalAllCommissionRate || 0)}%`} labelColor={PRIMARY} valueColor={PRIMARY} />
        <PropRow label="Tổng PDV giao dịch" value={money(form?.totals?.totalAllCommissionPrice || 0)} labelColor={PRIMARY} valueColor={PRIMARY} mt={10} />
      </Box>
    </Section>
  );
};

const SectionReferrals = ({ data, user }) => {
  const form = data?.referralsForm;
  if (!codeIn(user, [JOB.TRUONG_VUNG, JOB.TGD, JOB.TRUONG_KHOI])) return null;
  const list = form?.list || [];
  return (
    <Section title="Thông tin người giới thiệu">
      {list.map((block, bi) => (
        <Box key={bi} pb="16px" borderTop={bi !== 0 ? "0.8px solid" : "none"} borderColor={BLUE_LIGHT}>
          <PropRow label="Mã người giới thiệu:" value={codeOf(block)} mt={10} />
          <PropRow label="Tên người giới thiệu:" value={nameOf(block)} mt={10} />
          <PropRow label="Ngày công thực tế" value={block?.workingDays || block?.standardWorkingDaysInMonth || "0"} mt={10} />
          <PropRow label="Số tiền thường nóng" value={money(block?.commissionPrice || 0)} mt={10} />
        </Box>
      ))}
      {list.length > 0 ? (
        <Box borderTop="1px solid" borderColor={PRIMARY}>
          <Box h="10px" />
          <PropRow label="Tổng PDV giao dịch" value={money(form?.totals?.totalAllCommissionPrice || 0)} labelColor={PRIMARY} valueColor={PRIMARY} mt={10} />
        </Box>
      ) : (
        <PropRow label="Không có người giới thiệu" value="" labelColor={PRIMARY} size={1} />
      )}
    </Section>
  );
};

export const DetailTab = ({ data, kind, user }) => {
  const periods = getReconPeriods(data);
  const multi = periods.length > 1;
  const renderPeriod = (d) => (
    <>
      <SectionParticipants data={d} />
      <Box h="16px" />
      <ManagerGroupSection
        title="Thông tin giám đốc khối"
        form={d?.saleBlockManagersForm}
        prefix="GDK"
        totalRateLabel="Tổng tỷ lệ"
        totalRate={d?.saleBlockManagersForm?.totals?.totalSaleBlockManagerCommissionRate}
        totalPriceLabel="Tổng PDV"
        totalPrice={d?.saleBlockManagersForm?.totals?.totalSaleBlockManagerCommissionPrice}
        show={codeIn(user, [JOB.TRUONG_KHOI, JOB.TRUONG_VUNG, JOB.TGD])}
      />
      <Box h="16px" />
      <ManagerGroupSection
        title="Thông tin giám đốc vùng"
        form={d?.saleAreaManagersForm}
        prefix="GDV"
        totalRateLabel="Tổng tỷ lệ"
        totalRate={d?.saleAreaManagersForm?.totals?.totalSaleAreaManagerCommissionRate}
        totalPriceLabel="Tổng PDV"
        totalPrice={d?.saleAreaManagersForm?.totals?.totalSaleAreaManagerCommissionPrice}
        show={codeIn(user, [JOB.TRUONG_VUNG, JOB.TGD])}
      />
      {kind !== "provisional" && (
        <>
          <Box h="16px" />
          <SectionReferrals data={d} user={user} />
        </>
      )}
      <Box h="16px" />
      <SectionManagers data={d} user={user} />
    </>
  );
  return (
    <Box px="16px" pb="20px">
      <Box h="16px" />
      <Box w="100%" p="16px" borderRadius="8px" border="1px solid" borderColor={ORANGE}>
        <Text fontSize="12px" color={BLACK} fontStyle="italic">
          Tất cả các thông tin về thu nhập được liệt kê ở dưới chỉ mang tính chất tạm tính, sau khi hợp đồng mua bán được duyệt
          bởi bộ phận hỗ trợ bán hàng. Chuyên viên kinh doanh luôn phải đảm bảo cần cập nhật chuyên viên kinh doanh bán kèm và
          cộng tác viên đi kèm đúng thực tế để đảm bảo giao dịch chính xác.
        </Text>
        <Text fontSize="12px" color={BLACK} fontStyle="italic">Nếu có thắc mắc xin liên hệ với bộ phận có chức trách!</Text>
      </Box>

      {periods.map((d, idx) =>
        multi ? (
          <PeriodReconSection key={idx} title={reconPeriodLabel(d, idx)} index={idx}>
            {renderPeriod(d)}
          </PeriodReconSection>
        ) : (
          <React.Fragment key={idx}>{renderPeriod(d)}</React.Fragment>
        ),
      )}
    </Box>
  );
};

/* =========================================================
 * TAB 3 — TỔNG HỢP (Finals + FinalTotals)
 * =======================================================*/
const SectionFinals = ({ data }) => {
  const list = data?.finalEmployeesList?.list || [];
  return (
    <Section title="Tổng hợp PDV của từng thành viên" defaultOpen bare>
      {list.map((item, i) => (
        <Box
          key={i}
          p="16px"
          borderLeft="1px solid"
          borderRight="1px solid"
          borderBottom="1px solid"
          borderColor={BLUE_LIGHT}
          borderBottomRadius={i === list.length - 1 ? "8px" : "0"}
        >
          <PropRow label="Tên nhân viên" value={nameOf(item)} />
          <PropRow label="Mã nhân viên" value={codeOf(item)} mt={10} />
          <PropRow label="Chức vụ" value={item?.interpretation || ""} mt={10} />
          <PropRow label="Tổng PDV" value={money(item?.commissionPrice || 0)} mt={10} />
          <PropRow label="KT do đã ĐC kỳ trước/Đã tạm ứng/KT khác" value={money(item?.accountingFromPreviousPeriodOrAdvance || 0)} mt={10} />
          <PropRow label="Khấu trừ chứng chỉ/Ký quỹ" value={money(item?.certificateOrDeposit || 0)} mt={10} />
          <PropRow label="Tổng PDV còn lại sau KT" value={money(item?.remainingRevenueAfterAccounting || 0)} mt={10} />
          <PropRow label="Khấu trừ thuế thu nhập cá nhân" value={money(item?.personalIncomeTax || 0)} mt={10} />
          <PropRow label="Thu nhập" value={money(item?.remainingRevenueAfterTax || 0)} labelColor={PRIMARY} valueColor={PRIMARY} mt={10} />
        </Box>
      ))}
    </Section>
  );
};

const SectionFinalTotals = ({ data }) => {
  const t = data?.finalEmployeesList?.totals || {};
  return (
    <Section title="Tổng thu nhập" defaultOpen>
      <PropRow label="Tổng PDV" value={money(t?.commissionPrice || 0)} />
      <PropRow label="KT do đã ĐC kỳ trước/Đã tạm ứng/KT khác" value={money(t?.accountingFromPreviousPeriodOrAdvance || 0)} mt={10} />
      <PropRow label="Khấu trừ chứng chỉ/Ký quỹ" value={money(t?.certificateOrDeposit || 0)} mt={10} />
      <PropRow label="Tổng PDV còn lại sau KT" value={money(t?.remainingRevenueAfterAccounting || 0)} mt={10} />
      <PropRow label="Khấu trừ thuế thu nhập cá nhân" value={money(t?.personalIncomeTax || 0)} mt={10} />
      <PropRow label="Tổng thu nhập" value={money(t?.remainingRevenueAfterTax || 0)} labelColor={PRIMARY} valueColor={PRIMARY} mt={10} />
    </Section>
  );
};

export const SummaryTab = ({ data }) => {
  const periods = getReconPeriods(data);
  const multi = periods.length > 1;
  const renderPeriod = (d) => (
    <>
      <SectionFinals data={d} />
      <Box h="16px" />
      <SectionFinalTotals data={d} />
    </>
  );
  return (
    <Box pt="16px" px="16px" pb="20px">
      {periods.map((d, idx) =>
        multi ? (
          <PeriodReconSection key={idx} title={reconPeriodLabel(d, idx)} index={idx}>
            {renderPeriod(d)}
          </PeriodReconSection>
        ) : (
          <React.Fragment key={idx}>{renderPeriod(d)}</React.Fragment>
        ),
      )}
    </Box>
  );
};

/* =========================================================
 * TAB 4 — GIẢI NGÂN (chỉ chi trả)
 * =======================================================*/
const DisbursementMember = ({ member, period }) => {
  const [open, setOpen] = useState(true);
  const name = member?.employeeObject?.fullName || "";
  const code = member?.employeeObject?.code || "";
  const jobPositionName = member?.employeeObject?.jobPositionName ? `Chức vụ: ${member.employeeObject.jobPositionName}` : "";
  const title = `${name}${code ? ` (${code})` : ""}`;
  return (
    <Box pb="16px">
      <Box as="button" type="button" w="100%" pt="16px" onClick={() => setOpen((p) => !p)}>
        <Flex align="center">
          <Box w="40%"><Text fontSize="14px" fontWeight={600} color={SUBTITLE} textAlign="left">{jobPositionName}</Text></Box>
          <Box flex="1" display="flex" justifyContent="flex-end" alignItems="center" gap="4px">
            <Text fontSize="14px" fontWeight={600} color={BLACK} textAlign="right">{title}</Text>
            <Chevron open={open} />
          </Box>
        </Flex>
      </Box>
      <Collapse in={open} animateOpacity>
        <Box>
          <PropRow label="Tổng PDV sau thuế" value={money(member?.remainingRevenueAfterTax)} mt={10} />
          <PropRow label="Tổng PDV đã TT" value={money(member?.paidRemainingRevenueAfterTax)} mt={10} />
          <PropRow label="PDV còn lại" value={money(member?.unpaidRemainingRevenueAfterTax)} mt={10} />
        </Box>
      </Collapse>
    </Box>
  );
};

const PeriodBlock = ({ block }) => {
  const [open, setOpen] = useState(true);
  const members = block?.members || [];
  const totals = block?.totals || {};
  const dateStr = block?.createdAt ? fmtDate(block.createdAt) : "";
  const monthYears = block?.monthYears || [];
  const reconSuffix = monthYears.length ? ` - ĐC: ${monthYears.join(", ")}` : "";
  const periodTitle = `Thanh toán đợt ${block?.periodPayment}${dateStr ? ` - ${dateStr}` : ""}${reconSuffix}`;
  return (
    <Box mb="16px">
      <Flex
        as="button"
        type="button"
        w="100%"
        onClick={() => setOpen((p) => !p)}
        justify="space-between"
        align="center"
        p="16px"
        border="1px solid"
        borderColor={BLUE_LIGHT}
        borderTopRadius="8px"
        borderBottomRadius={open ? "0" : "8px"}
      >
        <Text fontSize="16px" fontWeight={600} color={BLACK} textAlign="left">{periodTitle}</Text>
        <Chevron open={open} />
      </Flex>
      <Collapse in={open} animateOpacity>
        <Box px="16px" borderLeft="1px solid" borderRight="1px solid" borderBottom="1px solid" borderColor={BLUE_LIGHT} borderBottomRadius="8px">
          {members.map((m, i) => (
            <Box key={i} borderTop={i !== 0 ? "0.8px solid" : "none"} borderColor={BLUE_LIGHT}>
              <DisbursementMember member={m} period={block?.periodPayment} />
            </Box>
          ))}
          <Box py="16px" borderTop="1px solid" borderColor={PRIMARY}>
            <PropRow label="Tổng PDV sau thuế" value={money(totals?.remainingRevenueAfterTax)} labelColor={PRIMARY} valueColor={PRIMARY} />
            <PropRow label="Tổng PDV đã TT" value={money(totals?.paidRemainingRevenueAfterTax)} labelColor={PRIMARY} valueColor={PRIMARY} mt={10} />
            <PropRow label="PDV còn lại" value={money(totals?.unpaidRemainingRevenueAfterTax)} labelColor={PRIMARY} valueColor={PRIMARY} mt={10} />
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

const NoDisbursement = () => (
  <Box pb="16px"><Text fontSize="16px" fontWeight={600} color={PRIMARY2}>Giao dịch này chưa được thanh toán</Text></Box>
);

const DisbursementBody = ({ data }) => {
  const [showTotal, setShowTotal] = useState(true);
  const periods = data?.periods || [];
  const totalsProject = data?.totalsProject || {};
  if (!periods.length) return <NoDisbursement />;
  return (
    <Box>
      {periods.map((p, idx) => (
        <PeriodBlock key={`${p.periodPayment}-${idx}`} block={p} />
      ))}
      <Flex
        as="button"
        type="button"
        w="100%"
        onClick={() => setShowTotal((p) => !p)}
        justify="space-between"
        align="center"
        p="16px"
        border="1px solid"
        borderColor={BLUE_LIGHT}
        borderTopRadius="8px"
        borderBottomRadius={showTotal ? "0" : "8px"}
      >
        <Text fontSize="16px" fontWeight={600} color={BLACK} textAlign="left">Tổng cộng PDV thanh toán</Text>
        <Chevron open={showTotal} />
      </Flex>
      <Collapse in={showTotal} animateOpacity>
        <Box p="16px" borderLeft="1px solid" borderRight="1px solid" borderBottom="1px solid" borderColor={BLUE_LIGHT} borderBottomRadius="8px">
          <PropRow label="Tổng PDV sau thuế" value={money(totalsProject?.remainingRevenueAfterTax)} labelColor={PRIMARY} valueColor={PRIMARY} />
          <PropRow label="Tổng PDV thanh toán" value={money(totalsProject?.paidRemainingRevenueAfterTax)} labelColor={PRIMARY} valueColor={PRIMARY} mt={10} />
          <PropRow label="Tổng PDV chưa thanh toán" value={money(totalsProject?.unpaidRemainingRevenueAfterTax)} labelColor={PRIMARY} valueColor={PRIMARY} mt={10} />
        </Box>
      </Collapse>
    </Box>
  );
};

export const DisbursementTab = ({ data }) => {
  const lockingId = data?.locking?._id;
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!lockingId) { setLoading(false); return; }
    setLoading(true);
    getIncomePaidDisbursementApi(lockingId)
      .then((d) => setInfo(d || null))
      .catch(() => setInfo(null))
      .finally(() => setLoading(false));
  }, [lockingId]);

  return (
    <Box pt="16px" px="16px" pb="20px">
      {loading ? (
        <Flex justify="center" py="40px"><Spinner color={PRIMARY} /></Flex>
      ) : info ? (
        <DisbursementBody data={info} />
      ) : (
        <NoDisbursement />
      )}
    </Box>
  );
};
